<script>
const {
    computed,
    reactive,
    ref
} = VueCompositionAPI
import { parseObject } from "utils/parsers"

const SERVER_ERRORS = parseObject({ id: "SIGNUP_ERRORS" })
const FIELD_VALUES = parseObject({ id: "FIELD_VALUES" })
const SIGNUP_URLS = parseObject({ id: "SIGNUP_URLS" })

import HideIcon from "Icons/HideIcon.vue"
import MinorCheckedCircleIcon from "Icons/MinorCheckedCircleIcon.vue"
import MinorXCircleIcon from "Icons/MinorXCircleIcon.vue"
import ShowIcon from "Icons/ShowIcon.vue"

export default {
    name: "SignupForm",
    components: {
        HideIcon,
        MinorCheckedCircleIcon,
        MinorXCircleIcon,
        ShowIcon,
    },
    setup () {
        /**
         * Gets hidden form fields from a DOM <template> element with ID 'signup-hidden-fields'
         * Used to retrieve additional signup form fields that are injected server-side
         * @type {string} The innerHTML content of the hidden fields element
         */

        const hiddenFields = document.getElementById( "signup-hidden-fields" ).innerHTML


        /**
         * Reactive array containing form field configurations for name, email and password
         * Each field has validation rules, error tracking, and display properties
         */

        const fields = reactive([
            {
                errors: {
                    server: [], // Server-side validation errors
                    ui: [] // Client-side validation errors
                },
                label: "Full name",
                name: "name",
                required: true,
                type: "text",
                validators: {
                    hasFirstAndLast: {
                        isValid: false,
                        message: "Enter a first and last name (e.g., 'Joe Smith').",
                        validate ( name ) {
                            // Split name on whitespace and verify first and last parts exist
                            const nameParts = name.trim().split( /\s+/ )

                            return nameParts.length >= 2
                                && nameParts[0].length > 0
                                && nameParts[1].length > 0
                        }
                    }
                },
                value: ""
            },
            {
                errors: {
                    server: [],
                    ui: []
                },
                label: "Email",
                name: "email",
                required: true,
                type: "email",
                validators: {
                    hasValidEmail: {
                        isValid: false,
                        message: "Enter a valid email address.",
                        validate ( email ) {
                            // Basic email validation regex
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

                            return emailRegex.test( email.trim() )
                        }
                    }
                },
                value: ""
            },
            {
                errors: {
                    server: [],
                    ui: []
                },
                label: "Password",
                name: "password",
                required: true,
                type: "password",
                validators: {
                    // Password must be at least 8 chars
                    minLength: {
                        isValid: false,
                        message: "At least 8 characters.",
                        validate: ( password ) => password.length >= 8
                    },
                    // Must contain at least one number
                    // eslint-disable-next-line vue/sort-keys
                    hasDigit: {
                        isValid: false,
                        message: "At least 1 digit, 0-9.",
                        validate: ( password ) => /\d/.test( password )
                    },
                    // Must contain at least one lowercase letter
                    hasLowercase: {
                        isValid: false,
                        message: "At least 1 lowercase letter, a-z.",
                        validate: ( password ) => /[a-z]/.test( password )
                    },
                    // Must contain at least one uppercase letter
                    hasUppercase: {
                        isValid: false,
                        message: "At least 1 uppercase letter, A-Z.",
                        validate: ( password ) => /[A-Z]/.test( password )
                    },
                    // Must contain at least one special character
                    // eslint-disable-next-line vue/sort-keys
                    hasSymbol: {
                        isValid: false,
                        message: 'At least 1 symbol: ()[]{}|\\~!@#$%^&*_-+=;:\'",<>./?',
                        validate: ( password ) => /[()[\]{}|\\~!@#$%^&*_\-+=;:'"<>,./?]/.test( password )
                    }
                },
                value: "",
            },
        ])


        fields.forEach( generateFieldAttributes )

        const disabledSubmitOnInitialLoad = ref( true )

        const hasUiErrors = computed(
            () => fields.some( ( field ) => field.errors.ui.length > 0 )
        )

        const isSubmitDisabled = computed( () => {
            const isDisabled = hasUiErrors.value

            return isDisabled || disabledSubmitOnInitialLoad.value
        })

        /**
         * Generates additional attributes for a form field by:
         * 1. Creating an ID from the field name
         * 2. Setting the field value from FIELD_VALUES if it exists and is not "False" or "None"
         * 3. Adding any server-side validation errors from SERVER_ERRORS to the field's error array
         * @param {Object} field - The form field object to modify
         * @param {string} field.name - The name of the field used to generate ID and lookup values/errors
         * @param {string} field.id - The ID to be generated for the field
         * @param {string} field.value - The value to be set on the field
         * @param {Object} field.errors - Object containing arrays of validation errors
         * @param {Array} field.errors.server - Array to store server-side validation errors
         */

        function generateFieldAttributes ( field ) {
            const fieldName = field.name

            field.id = `id_${ fieldName }`

            if ( FIELD_VALUES[fieldName] && FIELD_VALUES[fieldName] !== "False" && FIELD_VALUES[fieldName] !== "None" ) {
                field.value = FIELD_VALUES[fieldName]
            }

            if ( SERVER_ERRORS[fieldName]) {
                SERVER_ERRORS[fieldName].forEach(
                    ( error ) => field.errors.server.push( error )
                )
            }
        }

        /**
         * Reactive ref to track password visibility state
         * @type {import('vue').Ref<boolean>}
         */

        const showPassword = ref( false )

        /**
         * Toggles visibility of the password input field between plain text and masked characters
         * Stops event propagation, updates the showPassword state, finds the password field,
         * changes its input type, and refocuses the field
         * @param {Event} [e] - The DOM event object from the toggle button click
         * @returns {void}
         */

        function handleTogglePasswordVisibility ( e ) {
            e && e.stopPropagation()

            showPassword.value = !showPassword.value

            const passwordField = fields.find( ( field ) => field.name === "password" )

            if ( passwordField ) {
                passwordField.type = showPassword.value ? "text" : "password"

                document.querySelector( `#${ passwordField.id }` ).focus()
            }
        }

        /**
         * Reactive ref to track password validator popup visibility state
         * @type {import('vue').Ref<boolean>}
         */

        const showPasswordValidatorPopup = ref( false )

        /**
         * Toggles visibility of password validation requirements popup
         * Only triggers for password field interactions
         * If password field has a value, validates it immediately to show any validation issues
         * @param {Event} e - The DOM event object from the input element
         * @returns {void}
         */

        function togglePasswordValidatorPopup ( e ) {
            if ( e.target.name !== "password" ) return

            // if user has a value already in field and needs to change it,
            // we want to show validation issues at start
            testValidationOnInput( e )

            showPasswordValidatorPopup.value = !showPasswordValidatorPopup.value
        }

        /**
         * Sets a validation error message on a form field
         * Prevents the default form submission behavior and adds the browser's native
         * validation message to the field's UI errors array
         * @param {Event} e - The DOM event object from the form field that failed validation
         * @returns {void}
         */

        function setValidationMessage ( e ) {
            e.preventDefault()
            const input = e.target
            const field = fields.find(
                ({ name }) => name === input.name
            )

            field.errors.ui.push( input.validationMessage )
        }

        /**
         * Validates password field on input event
         * Only triggers validation for password field, ignoring other fields
         * @param {Event} e - The DOM event object from the input element
         * @returns {void}
         */

        function testValidationOnInput ( e ) {
            if ( e.target.name !== "password" ) return

            testValidity( e )
        }

        /**
         * Handles form field validation when a field loses focus
         * For password fields: hides password if currently visible
         * For other fields: validates the field value
         * @param {Event} e - The DOM event object from the input element that lost focus
         * @returns {void}
         */

        function testValidationOnBlur ( e ) {
            disabledSubmitOnInitialLoad.value = false

            if ( e.target.name === "password" ) {
                showPassword.value && handleTogglePasswordVisibility( e )

                return
            }
            else {
                testValidity( e )
            }
        }

        /**
         * Tests validity of a form field by running all its validators
         * Finds the field object matching the input element and validates its value
         * If the field has validators, runs each validator through setValidatorFacetValidity
         * @param {Event} e - The DOM event object from the input element
         * @returns {void}
         */

        function testValidity ( e ) {
            const input = e.target
            const field = fields.find(
                ({ name }) => name === input.name
            )
            const value = input.value

            if ( Object.keys( field.validators ).length ) {
                Object.values( field.validators ).forEach(
                    setValidatorFacetValidity( field, value )
                )
            }
        }

        /**
         * Creates a validator function that checks a single validation rule and updates field error state
         * @param {Object} field - The form field object containing error arrays and validation rules
         * @param {string} value - The current value of the field to validate
         * @returns {Function} A function that takes a validator object and:
         *   1. Clears existing UI errors
         *   2. Validates the value using the validator's rules
         *   3. If invalid, clears server errors and adds the validator's error message
         */

        function setValidatorFacetValidity ( field, value ) {
            field.errors.ui = []

            return ( validator ) => {
                validator.isValid = validator.validate( value )

                if ( !validator.isValid ) {
                    field.errors.server = []
                    field.errors.ui.push( validator.message )
                }
            }
        }

        return {
            SIGNUP_URLS,
            fields,
            handleTogglePasswordVisibility,
            hasUiErrors,
            hiddenFields,
            isSubmitDisabled,
            setValidationMessage,
            showPassword,
            showPasswordValidatorPopup,
            testValidationOnBlur,
            testValidationOnInput,
            togglePasswordValidatorPopup,
        }
    }
}
</script>

<template>
<form
    class="SignupForm"
    method="POST"
    :action="SIGNUP_URLS.signupForm"
>
    <div
        class="--a11y"
        v-html="hiddenFields"
    ></div>

    <template v-for="field in fields">
        <div
            class="SignupField"
            :key="field.label"
        >
            <label
                class="SignupFieldLabel"
                :for="field.id"
            >
                {{ field.label }}
            </label>

            <div class="SignupFieldInput">
                <input
                    required
                    class="SignupFieldControl"
                    v-model="field.value"
                    :class="{
                        hasError: !!field.errors.ui.length
                            || !!field.errors.server.length
                    }"
                    :id="field.id"
                    :name="field.name"
                    :type="field.type"
                    @blur="testValidationOnBlur"
                    @focus="togglePasswordValidatorPopup"
                    @input="testValidationOnInput"
                    @invalid="setValidationMessage"
                >

                <template v-if="field.name === 'password'">
                    <button
                        class="PasswordExposeControl"
                        type="button"
                        @click="handleTogglePasswordVisibility"
                    >
                        <template v-if="showPassword">
                            <hide-icon
                                class="PasswordExposeIcon"
                            ></hide-icon>
                            <span class="--a11y">Hide Password</span>
                        </template>
                        <template v-else>
                            <show-icon
                                class="PasswordExposeIcon"
                            ></show-icon>
                            <span class="--a11y">Show Password</span>
                        </template>
                    </button>
                </template>

                <template
                    v-if="field.name === 'password'"
                >
                    <aside class="SignupPopUp">
                        <h6>Password requirements</h6>

                        <ul class="SignupFieldErrorList">
                            <li
                                v-for="error in field.validators"
                                :key="error.message"
                            >
                                <label
                                    class="SignupFieldError"
                                    :class="{
                                        isValid: error.isValid
                                    }"
                                    :for="field.id"
                                >
                                    <template v-if="error.isValid">
                                        <minor-checked-circle-icon
                                            class="PositiveIcon"
                                        ></minor-checked-circle-icon>
                                    </template>
                                    <template v-else>
                                        <minor-x-circle-icon
                                            class="NegativeIcon"
                                        ></minor-x-circle-icon>
                                    </template>
                                    <span>{{ error.message }}</span>
                                </label>
                            </li>
                        </ul>
                    </aside>
                </template>

                <template v-if="field.errors.ui.length">
                    <ul class="SignupFieldErrorList">
                        <li
                            v-for="error in [field.errors.ui[0]]"
                            :key="error"
                        >
                            <label
                                class="SignupFieldError"
                                :for="field.id"
                            >
                                <minor-x-circle-icon
                                    class="NegativeIcon"
                                ></minor-x-circle-icon>
                                <span>{{ error }}</span>
                            </label>
                        </li>
                    </ul>
                </template>

                <template v-if="field.errors.server.length">
                    <ul class="SignupFieldErrorList">
                        <li
                            v-for="error in [field.errors.server[0]]"
                            :key="error"
                        >
                            <label
                                class="SignupFieldError"
                                :for="field.id"
                            >
                                <minor-x-circle-icon
                                    class="NegativeIcon"
                                ></minor-x-circle-icon>
                                <span>{{ error }}</span>
                            </label>
                        </li>
                    </ul>
                </template>
            </div>
        </div>
    </template>

    <div
        class="signup--controls"
    >
        <button
            class="btn button-primary signup--btn__wide"
            type="submit"
            :disabled="isSubmitDisabled"
        >
            <span>Sign Up</span>
        </button>

        <p>Already signed up? <a :href="SIGNUP_URLS.login">Login</a></p>
    </div>
</form>
</template>


<style>
.button-primary,
.button-tertiary {
    background: #151e46;
    border-color: #151e46;
    color: #fff;
    transition: background .25s ease-in-out;

    &:active,
    &:hover {
        background: #2d3559;
        border-color: #2d3559;
        color: #fff;

        & i {
            fill: currentColor;
        }
    }

    & i,
    svg#ico-github path {
        fill: currentColor;
        transition: background .25s ease-in-out;
    }
}

.button-secondary {
    background: #fed700;
    border-color: #fed700;
    color: #384062;
    transition: background .25s ease-in-out;

    &:active,
    &:hover {
        background: #fee133;
        border-color: #fee133;
        color: #384062;
    }
}

.PositiveIcon {
    fill: #4CA30D;
    width: 12px;
}

.NegativeIcon {
    fill: #D92D20;
}

.PasswordExposeIcon {
    stroke: #D3D3D3;
    width: 24px;
}

.SignupForm {
    display: flex;
    flex-direction: column;
    gap: 28px;
}

.SignupField {
    display: flex;
    flex-direction: column;
    gap: 6px;
    position: relative;
}

.SignupFieldLabel {
    color: #414452;
    font-size: 15px;
    font-weight: 600;
    line-height: 15px;
    margin: 0;
    padding-inline: 4px;
}

.SignupFieldInput {
    align-items: center;
    display: grid;
    grid-template-columns: 1fr auto 14px;
    grid-template-rows: auto auto;
    justify-content: center;
    position: relative;

    & > input {
        grid-column: 1 / span 3;
        grid-row: 1;
    }

    & > button {
        grid-column: 2;
        grid-row: 1;
    }

    & > .SignupFieldErrorList {
        align-self: flex-end;
        grid-column: 1 / span 3;
        grid-row: 2;
        justify-content: flex-start;
        position: absolute;
        translate: 4px 23px;
    }
}

.SignupFieldControl {
    border: 1px solid #EAECF0;
    border-radius: 8px;
    color: #121212;
    font-size: 15px;
    font-weight: 600;
    line-height: 16px;
    padding: 14px 18px;

    &:focus ~ .SignupPopUp {
        opacity: 1;
    }

    &:focus-visible {
        border-color: #0482EA;
        transition: border-color .5s ease-in-out;
    }

    &.hasError {
        border-color: #D92D20;
    }
}

.PasswordExposeControl {
    background: transparent;
    border: 0;
    display: inline-flex;
    outline: 0;
    padding: 4px;
    translate: -4px;
    width: fit-content;

    &:hover,
    &:active,
    &:focus {
        filter: contrast(0);
        outline: 0px;
        transition: filter .5s ease-in-out;
    }
}

.SignupFieldErrorList {
    color: #D92D20;
    display: flex;
    flex-direction: column;
    font-size: 11px;
    font-weight: 700;
    gap: 4px;
    letter-spacing: .02em;
    line-height: 20px;
    list-style: none;
    margin: 0;
    padding: 2px 4px 0;
}

.SignupFieldError {
    align-items: baseline;
    display: inline-flex;
    gap: 6px;
    justify-content: flex-start;
    margin: 0;

    &.isValid {
        color: #4CA30D;
    }

    & span {
        white-space: nowrap;
    }

    & svg {
        translate: 0 2px;
        width: 12px;
    }
}

.SignupPopUp {
    align-self: flex-start;
    background: #fff;
    border: 1px solid #EAECF0;
    border-radius: 9px;
    box-shadow: 0 5px 5px hsla(0, 0%, 0%, 25%);
    display: flex;
    flex-direction: column;
    gap: 4px;
    grid-column: 1 / span 3;
    justify-self: flex-end;
    opacity: 0;
    padding: 16px;
    pointer-events: none;
    position: absolute;
    translate: 100px 68px;
    transition: opacity .5s ease-in-out;
    width: fit-content;
    z-index: 100;

    & h6 {
        color: #414452;
        font-size: 12px;
        font-weight: 600;
        line-height: 15px;
    }

    & .SignupFieldErrorList {
        gap: 4px;
        margin: 0;
        padding: 0;
    }
}

@media (width < 736px) {
    .SignupPopUp {
        justify-self: center;
        translate: 0px -160px;
    }
}

.signup--controls {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 16px;

    & p {
        font-size: 14px;
        line-height: 24px;
        font-weight: 600;
    }

    & a {
        color: #006BC9;
        text-decoration: underline;
    }
}
</style>
